import { getWsBase, hasFallback, isLastFallback, switchToNextFallback } from './endpointManager';

type MessageHandler = (data: any) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private handlers: Map<string, MessageHandler[]> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 20;
  private _connected = false;
  private _currentToken: string | null = null;
  /** 連續失敗次數（達閾值時切換備援） */
  private _consecutiveFailures = 0;
  private _failoverThreshold = 3;

  get connected(): boolean {
    return this._connected;
  }

  constructor() {
    // 主端點恢復 → 重連到主端點
    window.addEventListener('endpoint-restored', () => {
      if (this._currentToken) {
        console.info('[WS] 主端點恢復，重新連線...');
        this.forceReconnect();
      }
    });
  }

  connect(token: string) {
    // 防止重複連線：OPEN 或 CONNECTING 中都不再建立新連線
    if (
      this.ws?.readyState === WebSocket.OPEN ||
      this.ws?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // 清理殘留的舊連線（CLOSING / CLOSED 狀態）
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }

    this._currentToken = token;

    // 認證改由 httpOnly cookie 攜帶（同源 WS 握手自動帶上），不再把 token 放 URL，
    // 避免 token 出現在伺服器/代理日誌。token 參數僅作重連判斷用的哨兵。
    const wsBase = getWsBase();
    const url = `${wsBase}/api/ws/realtime`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this._connected = true;
      this.reconnectAttempts = 0;
      this._consecutiveFailures = 0;
      this.emit('connection', { connected: true });
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        this.emit(msg.type, msg.data);
      } catch (e) {
        console.warn('[WS] JSON parse error:', e);
      }
    };

    this.ws.onclose = () => {
      this._connected = false;
      this.emit('connection', { connected: false });
      if (this._currentToken) {
        this.scheduleReconnect(this._currentToken);
      }
    };

    this.ws.onerror = () => {
      this._connected = false;
      this._consecutiveFailures++;
    };
  }

  disconnect() {
    this._currentToken = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._connected = false;
    this.reconnectAttempts = 0;
    this._consecutiveFailures = 0;
  }

  private scheduleReconnect(token: string) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;

    // 連續失敗達閾值 → 切換下一層備援
    if (
      this._consecutiveFailures >= this._failoverThreshold &&
      hasFallback() &&
      !isLastFallback()
    ) {
      console.warn('[WS] 連續 %d 次失敗，切換到下一層備援', this._consecutiveFailures);
      switchToNextFallback();
      this._consecutiveFailures = 0;
      this.reconnectAttempts = 0;
    }

    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => this.connect(token), delay);
  }

  /** 強制重連（端點切換時） */
  private forceReconnect() {
    const token = this._currentToken;
    if (!token) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._connected = false;
    this.reconnectAttempts = 0;
    this._consecutiveFailures = 0;
    setTimeout(() => this.connect(token), 500);
  }

  on(type: string, handler: MessageHandler) {
    const list = this.handlers.get(type) || [];
    list.push(handler);
    this.handlers.set(type, list);
  }

  off(type: string, handler: MessageHandler) {
    const list = this.handlers.get(type) || [];
    this.handlers.set(type, list.filter((h) => h !== handler));
  }

  private emit(type: string, data: any) {
    const list = this.handlers.get(type) || [];
    list.forEach((h) => h(data));
  }

  sendPing() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }
}

const wsService = new WebSocketService();
export default wsService;
